<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { chevronDown as chevronDownIcon } from 'ionicons/icons';

const { t } = useI18n();

interface Country {
	value: string;
	label: string;
	flagClass: string;
}

const props = defineProps<{
	modelValue: string;
	availableCountries: Country[];
}>();

const emit = defineEmits<{
	(e: 'update:modelValue', value: string): void;
}>();

const isOpen = ref(false);

const toggleDropdown = () => {
	isOpen.value = !isOpen.value;
};

const selectCountry = (value: string) => {
	emit('update:modelValue', value);
	isOpen.value = false;
};

const selectedOption = computed(() => props.availableCountries.find((c) => c.value === props.modelValue));

onMounted(() => {
	if (!props.modelValue && props.availableCountries.length > 0) {
		emit('update:modelValue', props.availableCountries[0].value);
	}
});

// Close dropdown when clicking outside
document.addEventListener('click', (e) => {
	const path = e.composedPath?.() || [];
	if (!path.some((el: any) => el?.classList?.contains('relative'))) {
		isOpen.value = false;
	}
});
</script>

<template>
	<div class="relative w-full pb-2">
		<div class="flex items-center justify-between px-4 py-2 rounded cursor-pointer text-white" @click="toggleDropdown">
			<div class="flex items-center gap-2">
				<span v-if="selectedOption" :class="selectedOption.flagClass" style="width: 1.5em; height: 1em; display: inline-block"></span>
				<span>{{ selectedOption?.label || t('top_up.country_select') }}</span>
			</div>
			<ion-icon :icon="chevronDownIcon" />
		</div>

		<transition name="dropdown-fade">
			<div v-if="isOpen" class="absolute z-10 w-full mt-1 bg-black rounded max-h-60 overflow-auto">
				<div v-for="country in availableCountries" :key="country.value" class="flex items-center gap-2 px-4 py-2 hover:bg-white/10 cursor-pointer text-white" @click="selectCountry(country.value)">
					<span :class="country.flagClass" style="width: 1.5em; height: 1em; display: inline-block"></span>
					<span>{{ t('countries.' + country.value) }}</span>
				</div>
			</div>
		</transition>
	</div>
</template>

<style scoped>
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
	transition: all 0.2s ease;
}
.dropdown-fade-enter-from {
	opacity: 0;
	transform: translateY(-5px);
}
.dropdown-fade-enter-to {
	opacity: 1;
	transform: translateY(0);
}
.dropdown-fade-leave-from {
	opacity: 1;
	transform: translateY(0);
}
.dropdown-fade-leave-to {
	opacity: 0;
	transform: translateY(-5px);
}
</style>
